import { useEffect, useState } from 'react';
import { buildApiUrl, getApiHeaders } from '../../support/queriesHelpers';
import { API_BASE } from './mediaUtils';

const urlCache = new Map<string, { url: string; expiresAt: number }>();

export function cachedMediaUrl(postId: string, mid: number): string | null {
  const hit = urlCache.get(`${postId}/${mid}`);
  return hit && hit.expiresAt > Date.now() ? hit.url : null;
}

export function useMediaItemUrl(postId: string, mid: number, enabled: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const key = `${postId}/${mid}`;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async (retry = 0) => {
      try {
        const path = `${API_BASE}/posts/${postId}/media/${mid}/url/`;
        const res = await fetch(buildApiUrl(path), { headers: getApiHeaders(null, 'GET', path) });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const refreshIn = (data.refresh_after ?? 3300) * 1000;
        urlCache.set(key, { url: data.url, expiresAt: Date.now() + refreshIn });
        setUrl(data.url);
        setError(false);
        timer = setTimeout(() => load(), refreshIn);
      } catch {
        if (cancelled) return;
        if (retry < 1) timer = setTimeout(() => load(retry + 1), 3000);
        else setError(true);
      }
    };

    const cached = cachedMediaUrl(postId, mid);
    setError(false);
    if (cached) {
      setUrl(cached);
      timer = setTimeout(() => load(), urlCache.get(key)!.expiresAt - Date.now());
    } else {
      setUrl(null);
      load();
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [postId, mid, enabled]);

  return { url, error };
}
